import { useState } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { twMerge } from 'tailwind-merge';

export default function TeamRadialSelector({
  teams,
  onChange = () => {},
}: {
  teams?: {
    id: string;
    name: string;
  }[];
  onChange: (teamId: string) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);

  const toggleMenu = (state?: boolean) => {
    const endState = state ?? !showMenu;
    setShowMenu(endState);
    if (endState) {
      setZIndex('z-50');
    } else {
      setTimeout(() => {
        setZIndex('z-0');
      }, 700);
    }
  };

  const teamCount = teams?.length ?? 0;

  const baseSize = 64;
  const circleSize = teamCount > 9 ? baseSize * (9 / teamCount) : baseSize;
  const calculateRadius = (circumference: number) => {
    return circumference / (2 * Math.PI);
  };
  const buffer = circleSize / 10;
  const distance = Math.max(
    56,
    calculateRadius(circleSize * teamCount) + buffer
  );
  const fullDiameter = distance * 2 + circleSize * 1.5;
  const calcTransitionDelay = (index: number) => (index + 1) ** 2 * 5;
  const calcTotalDuration = (totalItems: number) =>
    calcTransitionDelay(totalItems) + 100;
  const calcStyle = (index: number) => {
    const angle = (2 * Math.PI * index) / teamCount - Math.PI / 2;
    const x = Math.cos(angle) * (showMenu ? distance : 0);
    const y = Math.sin(angle) * (showMenu ? distance : 0);

    return {
      height: `${circleSize}px`,
      width: `${circleSize}px`,
      transform: `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${
        showMenu ? 1 : 0
      }) `,
      transitionDelay: `${calcTransitionDelay(index)}ms`,
    };
  };
  const [zIndex, setZIndex] = useState('z-0');

  return (
    <div
      className={twMerge(
        'radial-menu relative transition-all duration-300 ease-in-out ',
        zIndex
      )}
    >
      <button
        className={twMerge(
          'relative rounded-full size-8 grid place-content-center text-center bg-teal-400 transition-transform duration-300',
          showMenu ? ' -rotate-45' : 'rotate-0'
        )}
        onClick={() => toggleMenu(true)}
        type="button"
      >
        ➕
      </button>
      <ClientOnly>
        {() => (
          <>
            <div
              className={twMerge(
                'absolute z-[9999] left-0   top-1/2 -translate-y-1/2 transition-all  rounded-full ',
                showMenu
                  ? 'rotate-0 ease-out pointer-events-auto '
                  : 'rotate-45  ease-in pointer-events-none'
              )}
              style={{
                width: `${fullDiameter * 1.5}px`,
                height: `${fullDiameter * 1.5}px`,
                transitionDuration: `${calcTotalDuration(teamCount)}ms`,
              }}
            >
              <span
                className={twMerge(
                  'absolute z-50 pointer-events-none inset-0 mix-blend-color-burn backdrop-blur rounded-full bg-[radial-gradient(circle_closest-side,black_30%,rgba(25,0,0,0)_100%)] transition-all  ease-in-out',
                  showMenu
                    ? 'opacity-100 scale-1 backdrop-blur'
                    : 'scale-0 opacity-0'
                )}
                style={{
                  width: `${fullDiameter * 1.5}px`,
                  height: `${fullDiameter * 1.5}px`,
                  transitionDuration: `${calcTotalDuration(teamCount)}ms`,
                }}
              ></span>
              <button
                className={twMerge(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 rounded-full size-8 grid place-content-center text-center',

                  showMenu ? 'opacity-100 rotate-0' : '-rotate-45 opacity-0'
                )}
                onClick={() => toggleMenu(false)}
                type="button"
              >
                🚫
              </button>
              {teams?.map((item, index) => (
                <button
                  key={item.id}
                  className={twMerge(
                    'radial-menu-item rounded-full [filter:drop-shadow(0_0_8px_theme(colors.teal.500))] border-teal-800 border-1 ring-inset ring-teal-400 ring-2  bg-teal-400   bg-gradient-to-br from-teal-200 to-teal-600 z-50 text-black [text-shadow:0px_1px_0px_rgba(255,255,255,0.6)] left-1/2 top-1/2 absolute grid place-content-center text-center text-xs transform-all duration-300',
                    showMenu
                      ? 'translate-y-0 scale-100 opacity-100 pointer-events-auto visible ease-out'
                      : '-translate-y-1/2 scale-0 opacity-0 pointer-events-none invisible ease-in'
                  )}
                  style={calcStyle(index)}
                  onClick={() => {
                    onChange?.(item.id);
                    setShowMenu(false);
                  }}
                  type="button"
                >
                  {item.name}
                </button>
              ))}
            </div>

            <div
              className={twMerge(
                'fixed inset-0 backdrop-blur z-40 pointer-events-none transition-all duration-200 ease-linear',
                showMenu ? 'opacity-100' : 'opacity-0'
              )}
            ></div>
          </>
        )}
      </ClientOnly>
    </div>
  );
}
